import { onContentLibraryFileViewedSaga } from 'features/content-library/sagas/onContentLibraryFileViewedSaga';
import { takeEvery } from 'redux-saga/effects';
import { createLibraryFolderSaga } from 'features/content-library/sagas/createLibraryFolderSaga';
import {
  cancelContentLibraryFileProcessingRequested,
  contentLibraryChangeFilePageRequested,
  contentLibraryCopyFileRequested,
  contentLibraryFileOpenRequested,
  contentLibraryFilePreviewClosed,
  contentLibraryFilePreviewRequested,
  contentLibraryFileShareRequested,
  contentLibraryFileUnshareRequested,
  contentLibraryFileUploadRequested,
  createContentFolderRequested,
  createEmbedContentRequested,
  deleteContentLibraryFileRequested,
  deleteContentLibraryFolderRequested,
  downloadContentLibraryFileRequested,
  fileViewed,
  moveContentFolderRequested,
  moveContentLibraryFileRequested,
  renameContentFolderRequested,
  renameContentLibraryFileRequested,
  signalingContentLibraryFileClosed,
  signalingContentLibraryFileMoved,
  signalingContentLibraryFileOpened,
  signalingContentLibraryFilePageChanged,
  signalingContentLibraryFolderMoved,
  signalingContentLibraryOpenedFilesReceived,
  signalingContentLibraryMediaPlayerStateChanged,
  whiteboardViewed,
} from 'features/content-library/actions';
import { renameLibraryFolderSaga } from 'features/content-library/sagas/renameLibraryFolderSaga';
import { renameContentLibraryFileSaga } from 'features/content-library/sagas/renameContentLibraryFileSaga';
import { deleteContentLibraryFileSaga } from 'features/content-library/sagas/deleteContentLibraryFileSaga';
import { deleteContentLibraryFolderSaga } from 'features/content-library/sagas/deleteContentLibraryFolderSaga';
import { uploadContentLibraryFilesSaga } from 'features/content-library/sagas/uploadContentLibraryFilesSaga';
import {
  contentLibrarySortByChanged,
  embedPlayerUserAction,
  fileClosed,
} from 'features/content-library/contentLibrarySlice';
import { persistContentLibrarySortBySaga } from 'features/content-library/sagas/persistContentLibrarySortBySaga';
import { cancelContentLibraryFileSaga } from 'features/content-library/sagas/cancelContentLibraryFileSaga';
import { openContentLibraryFileSaga } from 'features/content-library/sagas/openContentLibraryFileSaga';
import { onContentLibraryFileOpenedSaga } from 'features/content-library/sagas/onContentLibraryFileOpenedSaga';
import { onContentLibraryFileClosedSaga } from 'features/content-library/sagas/onContentLibraryFileClosedSaga';
import { closeContentLibraryFileSaga } from 'features/content-library/sagas/closeContentLibraryFileSaga';
import { changeContentLibraryFilePageSaga } from 'features/content-library/sagas/changeContentLibraryFilePageSaga';
import { createLibraryEmbedContentSaga } from 'features/content-library/sagas/createLibraryEmbedContentSaga';
import { downloadContentLibraryFileSaga } from 'features/content-library/sagas/downloadContentLibraryFileSaga';
import { embedPlayerUserActionSaga } from 'features/content-library/sagas/embedPlayerUserActionSaga';
import { onContentLibraryMediaPlayerRemoteStateChangedSaga } from 'features/content-library/sagas/onContentLibraryMediaPlayerRemoteStateChangedSaga';
import { requestPreviewContentLibraryFileSaga } from 'features/content-library/sagas/requestPreviewContentLibraryFileSaga';
import { closePreviewContentLibraryFileSaga } from 'features/content-library/sagas/closePreviewContentLibraryFileSaga';
import { onContentLibraryOpenedFilesReceived } from 'features/content-library/sagas/onContentLibraryOpenedFilesReceived';
import { streamMaximized, streamMinimized } from 'features/layout/actions';
import { onStreamMinimizedSaga } from 'features/content-library/sagas/onStreamMinimizedSaga';
import { onStreamMaximizedSaga } from 'features/content-library/sagas/onStreamMaximizedSaga';
import { onContentLibraryFilePageChangedSaga } from 'features/content-library/sagas/onContentLibraryFilePageChangedSaga';
import { onWhiteboardViewedSaga } from 'features/content-library/sagas/onWhiteboardViewedSaga';
import { signalingRoomJoined } from 'features/room/actions';
import { configureActiveLibraryTypeSaga } from 'features/content-library/sagas/configureActiveLibraryTypeSaga';
import { shareContentLibraryFileSaga } from 'features/content-library/sagas/shareContentLibraryFileSaga';
import { unshareContentLibraryFileSaga } from 'features/content-library/sagas/unshareContentLibraryFileSaga';
import { copyContentLibraryFileSaga } from 'features/content-library/sagas/copyContentLibraryFileSaga';
import { moveContentLibraryFolderSaga } from 'features/content-library/sagas/moveContentLibraryFolderSaga';
import { moveContentLibraryFileSaga } from 'features/content-library/sagas/moveContentLibraryFileSaga';
import { onContentLibraryItemMovedSaga } from 'features/content-library/sagas/onContentLibraryItemMovedSaga';

export default function* contentLibraryWatcher() {
  // manage
  yield takeEvery(createContentFolderRequested, createLibraryFolderSaga);
  yield takeEvery(renameContentFolderRequested, renameLibraryFolderSaga);
  yield takeEvery(deleteContentLibraryFolderRequested, deleteContentLibraryFolderSaga);
  yield takeEvery(renameContentLibraryFileRequested, renameContentLibraryFileSaga);
  yield takeEvery(deleteContentLibraryFileRequested, deleteContentLibraryFileSaga);
  yield takeEvery(moveContentFolderRequested, moveContentLibraryFolderSaga);
  yield takeEvery(moveContentLibraryFileRequested, moveContentLibraryFileSaga);
  yield takeEvery(
    [signalingContentLibraryFileMoved, signalingContentLibraryFolderMoved],
    onContentLibraryItemMovedSaga
  );

  yield takeEvery(createEmbedContentRequested, createLibraryEmbedContentSaga);

  // upload
  yield takeEvery(contentLibraryFileUploadRequested, uploadContentLibraryFilesSaga);
  yield takeEvery(cancelContentLibraryFileProcessingRequested, cancelContentLibraryFileSaga);

  // open file
  yield takeEvery(contentLibraryFileOpenRequested, openContentLibraryFileSaga);
  yield takeEvery(contentLibraryChangeFilePageRequested, changeContentLibraryFilePageSaga);
  yield takeEvery(signalingContentLibraryFileOpened, onContentLibraryFileOpenedSaga);
  yield takeEvery(signalingContentLibraryFilePageChanged, onContentLibraryFilePageChangedSaga);

  yield takeEvery(contentLibraryFilePreviewRequested, requestPreviewContentLibraryFileSaga);
  yield takeEvery(contentLibraryFilePreviewClosed, closePreviewContentLibraryFileSaga);

  yield takeEvery(signalingContentLibraryOpenedFilesReceived, onContentLibraryOpenedFilesReceived);

  yield takeEvery(signalingContentLibraryFileClosed, onContentLibraryFileClosedSaga);
  yield takeEvery(fileClosed, closeContentLibraryFileSaga);
  yield takeEvery(fileViewed, onContentLibraryFileViewedSaga);
  yield takeEvery(whiteboardViewed, onWhiteboardViewedSaga);

  // share file
  yield takeEvery(contentLibraryFileShareRequested, shareContentLibraryFileSaga);
  yield takeEvery(contentLibraryFileUnshareRequested, unshareContentLibraryFileSaga);

  yield takeEvery(contentLibraryCopyFileRequested, copyContentLibraryFileSaga);

  yield takeEvery(downloadContentLibraryFileRequested, downloadContentLibraryFileSaga);

  yield takeEvery(embedPlayerUserAction, embedPlayerUserActionSaga);
  // yield takeEvery(embedPlayerReady, embedPlayerUserActionSaga);
  yield takeEvery(
    signalingContentLibraryMediaPlayerStateChanged,
    onContentLibraryMediaPlayerRemoteStateChangedSaga
  );

  yield takeEvery(streamMinimized, onStreamMinimizedSaga);
  yield takeEvery(streamMaximized, onStreamMaximizedSaga);

  yield takeEvery(signalingRoomJoined, configureActiveLibraryTypeSaga);
  yield takeEvery(contentLibrarySortByChanged, persistContentLibrarySortBySaga);
}
